import { createFileRoute, notFound, redirect, useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useState } from "react"
import type { TranscriptResponse } from "../../utils/conversation-store.ts"
import ChatInterface from "../components/ChatInterface"
import FlowStateEditor, { createEntry, type FlowStateEntry, parseEntryValue } from "../components/FlowStateEditor.tsx"
import { API_BASE_URL } from "../config"
import { usePendingMessages } from "../contexts/PendingMessageContext.tsx"
import { useChatSession } from "../hooks/useChatSession"
import { toast } from "../utils/toast.tsx"

export const Route = createFileRoute("/conversations/$conversationId")({
  pendingComponent: () => null,
  beforeLoad: ({ search }) => {
    if (!search.agent) {
      throw redirect({ to: "/new", search, replace: true })
    }
  },
  loader: async ({ params }) => {
    const res = await fetch(`${API_BASE_URL}/api/conversations/${params.conversationId}/transcript`, {
      credentials: "include"
    })
    if (!res.ok) {
      if (res.status === 404) {
        throw notFound({ routeId: "__root__" })
      }
      throw new Error("Failed to load transcript")
    }
    const data = (await res.json()) as TranscriptResponse
    return { transcript: data.transcript }
  },
  component: ConversationPage
})

function ConversationPage() {
  const { conversationId } = Route.useParams()
  return <ConversationPageContent key={conversationId} />
}

function ConversationPageContent() {
  const { conversationId } = Route.useParams()
  const { transcript } = Route.useLoaderData()
  const [input, setInput] = useState("")
  const {
    devTools: devtoolsParam,
    agent,
    backgroundMode,
    initialFlowContext: initialFlowContextParam
  } = Route.useSearch({ structuralSharing: true })
  const navigate = useNavigate({ from: "/conversations/$conversationId" })
  const { messages: pendingMessages, popMessage } = usePendingMessages()

  const startingFlowContextEntries = useMemo<FlowStateEntry[]>(() => {
    if (!initialFlowContextParam) return []
    try {
      const parsed = JSON.parse(initialFlowContextParam) as { key: string; value: string }[]
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map((e) => createEntry(e.key, e.value))
      }
      return []
    } catch {
      toast.error("Invalid initial flow context", {
        description: "Could not parse the initial flow context parameter"
      })
      return []
    }
  }, [initialFlowContextParam])

  const parsedInitialFlowContext = useMemo<Record<string, unknown>>(() => {
    const result: Record<string, unknown> = {}
    for (const entry of startingFlowContextEntries) {
      const key = entry.key.trim()
      if (key) {
        result[key] = parseEntryValue(entry.value)
      }
    }
    return result
  }, [startingFlowContextEntries])

  const {
    messages: chatMessages,
    currentResponse,
    isLoading,
    stepHistory,
    stepPath,
    flowState,
    currentToolCall,
    clarification,
    currentThought,
    sendMessage,
    submitClarification,
    dismissClarification,
    attachments,
    uploadFile,
    removeAttachment,
    isUploading
  } = useChatSession({
    agentName: agent as string,
    backgroundMode,
    conversationId,
    initialMessages: transcript,
    initialFlowContext: parsedInitialFlowContext
  })

  useEffect(() => {
    if (pendingMessages.length > 0 && !isLoading) {
      const pending = popMessage()
      if (pending) {
        sendMessage(pending.text, pending.attachmentIds)
      }
    }
  }, [pendingMessages, popMessage, isLoading, sendMessage])

  const handleSend = useCallback(() => {
    if (!input.trim() && attachments.length === 0) return
    sendMessage(input.trim())
    setInput("")
  }, [input, sendMessage, attachments])

  const handleCloseDevTools = useCallback(() => {
    navigate({
      to: ".",
      search: (prev) => ({ ...prev, devTools: undefined })
    })
  }, [navigate])

  const transcriptDownloadUrl = `${API_BASE_URL}/api/conversations/${conversationId}/transcript/download`
  const transcriptDownloadFilename = `transcript-${conversationId}.json`

  return (
    <ChatInterface
      input={input}
      setInput={setInput}
      flowState={flowState}
      messages={chatMessages}
      currentResponse={currentResponse}
      isLoading={isLoading}
      transcriptDownloadUrl={transcriptDownloadUrl}
      transcriptDownloadFilename={transcriptDownloadFilename}
      onSend={handleSend}
      stepPath={stepPath}
      stepHistory={stepHistory}
      currentToolCall={
        currentToolCall
          ? {
              id: currentToolCall.toolCallId ?? "",
              name: currentToolCall.toolName,
              arguments: currentToolCall.toolArguments ?? {}
            }
          : undefined
      }
      clarification={clarification}
      onSubmitClarification={submitClarification}
      onCloseClarification={dismissClarification}
      currentThought={currentThought}
      devToolsMode={devtoolsParam}
      startingFlowStateEditor={
        devtoolsParam && startingFlowContextEntries.length > 0 ? (
          <FlowStateEditor entries={startingFlowContextEntries} onChange={() => {}} title="Initial Flow Context" />
        ) : undefined
      }
      onCloseDevTools={handleCloseDevTools}
      attachments={attachments}
      onAttach={uploadFile}
      onRemoveAttachment={removeAttachment}
      isUploading={isUploading}
    />
  )
}
